/* mears-mcmview-override-contents

// NB: We re-export this for TypeScript so that it doesn't complain about UseRestClientContextService not being a Client. We don't need Client at all, but we do need type checks to pass in MCMView.

import { UseRestClientContextService } from '../services/use-rest-client-context.service';

export default UseRestClientContextService;
*/

import env from "react-dotenv";
import download from "downloadjs";
import queryString from "query-string";

import Property, { PropertyImage } from "./Property";
import {
	Accessor,
	Export,
	Filter,
	PropertyAttribute,
	PropertyFilter,
	Scalar,
	SmartTarget,
	V3PropertyFilter,
} from "../types/types";
import Team from "./Team";
import IClient, {
	AllSmartTargetParameters,
	IPropertyDownloadQuery,
	LegacyPropertyQuery,
	V3PropertyQuery,
} from "./IClient";
import { Properties } from "./responses/Properties";
import { PropertyQueryResponse } from "./responses/PropertyQueryResponse";
import HttpError from "../errors/HttpError";
import { PaginationState, SortingState } from "@tanstack/react-table";
import { Aggregates } from "./responses/Aggregates";
import { EditableFieldValueCollection } from "../contexts/PropertyEditContext";
import { FetchReportResponse, Report } from "../types/reports";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
type QueryParameterObject = Record<string, Scalar | Scalar[] | undefined>;
const HttpStatusCodes = {
	NO_CONTENT: 204,
};

// NB: This is shared with HEERO. Maybe we should make this into a package

function isObject(item: any) {
	return item && typeof item === "object" && !Array.isArray(item);
}

// TODO: Probably best to find a type safe library that will do this
// @ts-ignore
function mergeDeep(target, ...sources) {
	if (!sources.length) return target;
	const source = sources.shift();

	if (isObject(target) && isObject(source)) {
		for (const key in source) {
			if (isObject(source[key])) {
				if (!target[key]) {
					Object.assign(target, { [key]: {} });
				} else {
					target[key] = Object.assign({}, target[key]);
				}
				mergeDeep(target[key], source[key]);
			} else {
				Object.assign(target, { [key]: source[key] });
			}
		}
	}

	return mergeDeep(target, ...sources);
}

export default class Client implements IClient {
	private readonly jwtIdToken: string;

	constructor(jwtIdToken: string) {
		if (!/^([a-zA-Z0-9_=]+)\.([a-zA-Z0-9_=]+)?\.([a-zA-Z0-9_\-+/=]*)/.test(jwtIdToken))
			throw new Error("Invalid JWT");

		this.jwtIdToken = jwtIdToken;
	}

	private addRequestParams(json: any, params: any) {
		const defaults = {
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
			},
		};

		if (!params) params = {};

		mergeDeep(params, defaults);

		params.body = JSON.stringify(json);

		return params;
	}

	private async request(method: HttpMethod, resource: string, params: any): Promise<Response> {
		const options = {
			headers: {
				Authorization: `Bearer ${this.jwtIdToken}`,
			},
			method: method,
			signal: null,
			body: null,
		};

		if (params) mergeDeep(options, params);

		// NB: Workaround to preserve type for AbortSignals. Look at a better strategy such as https://github.com/patrickkunka/helpful-merge
		if (params?.signal) options.signal = params.signal;

		// NB: Workaround to preserve type for FormData. Look at a better strategy such as https://github.com/patrickkunka/helpful-merge
		if (params?.body instanceof FormData) options.body = params.body;

		const response = await fetch(`${env.API_V2_BASE_URL}${resource}`, options);
		if (!response.ok)
			return response.text().then((message) => {
				throw new Error(response.statusText + " - " + message); // NB: Throw 404's etc as errors
			});
		return response;
	}

	private get(resource: string, params?: any): Promise<any> {
		return this.request("GET", resource, params);
	}

	private post(resource: string, json: any, params?: any): Promise<any> {
		return this.request("POST", resource, this.addRequestParams(json, params));
	}

	private put(resource: string, json?: any, params?: any): Promise<any> {
		return this.request("PUT", resource, this.addRequestParams(json, params));
	}

	private patch(resource: string, json?: any, params?: any): Promise<any> {
		return this.request("PATCH", resource, this.addRequestParams(json, params));
	}

	private destroy(resource: string, json?: any, params?: never): Promise<any> {
		return this.request("DELETE", resource, this.addRequestParams(json, params));
	}
	private async downloadFile(respPromise: Promise<any>, mimeType: string): Promise<any> {
		const response = await respPromise;
		const disposition = response.headers.get("Content-Disposition");
		let filename = disposition!.split(/;(.+)/)[1].split(/=(.+)/)[1];
		if (filename.toLowerCase().startsWith("utf-8''")) {
			filename = decodeURIComponent(filename.replace("utf-8''", ""));
		} else {
			filename = filename.replace(/['"]/g, "");
		}
		const data = await response.blob();
		download(data, filename, mimeType);
	}

	private download(resource: string, mimeType: string): Promise<void> {
		return this.downloadFile(this.get(resource), mimeType);
	}
	private downloadPost(resource: string, mimeType: string, json: any): Promise<void> {
		return this.downloadFile(this.post(resource, json), mimeType);
	}

	private upload(resource: string, formData: FormData) {
		if (!(formData instanceof FormData)) throw new Error("Argument must be an instance of FormData");

		return this.request("POST", resource, {
			body: formData,
		});
	}

	fetchPropertyFromUprn(uprn: string, teamId: string | number) {
		return this.get(`/api/teams/${teamId}/properties?uprn=${uprn}`);
	}

	/**
	 * @deprecated Use v3.getProperties instead
	 */
	fetchTeamProperties(team: Team | number, query?: LegacyPropertyQuery, signal?: AbortSignal) {
		const params: LegacyPropertyQuery = query ?? {};
		const qstr = queryString.stringify(params, {
			arrayFormat: "bracket",
		});
		const options: { signal?: AbortSignal } = {};

		if (signal) options.signal = signal;

		const id: number = team instanceof Team ? team.id : team;

		return this.get(`/api/teams/${id}/properties?${qstr}`, options);
	}

	get v3() {
		const self = this;

		// by default, keys with a value of undefined will get skipped
		const query = (params: QueryParameterObject) => queryString.stringify(params, { arrayFormat: "bracket" });

		async function request<T>(method: HttpMethod, uri: string, payload?: any, options?: RequestInit): Promise<T> {
			let response: Response;

			switch (method) {
				case "GET":
				case "DELETE":
					if (payload) {
						const qstr = query(payload);

						uri += "?" + qstr;
					}

					if (method === "GET") response = await self.get(uri, options);
					else response = await self.destroy(uri);
					break;

				case "POST":
					response = await self.post(uri, payload);
					break;

				case "PUT":
					response = await self.put(uri, payload);
					break;

				case "PATCH":
					response = await self.patch(uri, payload);
					break;

				default:
					throw new Error("Method not implemented");
			}

			if (!response.ok) throw new HttpError(response);
			if (response.status === HttpStatusCodes.NO_CONTENT) return undefined as T;

			return (await response.json()) as T;
		}

		const createPropertyQuery = async (
			team: Team,
			accessors: Accessor[] | "*" | undefined,
			propertyAttributes: PropertyAttribute[] | "*" | undefined,
			signal: AbortSignal,
			ids?: number[],
			groupId?: number,
			arrangement?: (Accessor | PropertyAttribute)[]
		) =>
			request<PropertyQueryResponse>(
				"POST",
				`/api/v3/teams/${team.id}/properties/query`,
				{
					accessors,
					property_attributes: propertyAttributes,
					ids,
					group: groupId,
					arrangement,
				},
				{
					signal,
				}
			);

		const generatePropertyQueryParams = (
			pagination: PaginationState | false,
			options?: {
				filters?: V3PropertyFilter[];
				sorting?: SortingState;
				quickSearch?: string;
				accessors?: Accessor[];
				propertyAttributes?: PropertyAttribute[];
			}
		) => {
			const params: V3PropertyQuery = {
				page_number: 1,
				page_size: Math.pow(2, 32),
			};

			if (pagination !== false) {
				params.page_number = pagination.pageIndex + 1;
				params.page_size = pagination.pageSize;
			}

			if (options?.filters?.length) {
				params.filters = JSON.stringify(options.filters);
			}

			if (options?.sorting) params.orderBy = JSON.stringify(options.sorting);

			if (options?.quickSearch) params.quick_search = options.quickSearch;

			if (options?.accessors?.length) params.accessors = options.accessors;

			if (options?.propertyAttributes?.length) params.property_attributes = options.propertyAttributes;

			return params;
		};

		const getPropertiesViaQuery = async (
			team: Team,
			queryId: string,
			filters: V3PropertyFilter[] | undefined,
			sorting: SortingState | undefined,
			pagination: PaginationState | false,
			quickSearch: string | undefined,
			signal: AbortSignal
		) => {
			const params = generatePropertyQueryParams(pagination, { filters, sorting, quickSearch });

			return request<Properties>("GET", `/api/v3/teams/${team.id}/properties/query/${queryId}`, params, {
				signal,
			});
		};

		const getProperties = (
			team: Team,
			accessors: Accessor[],
			propertyAttributes: PropertyAttribute[],
			filters: V3PropertyFilter[] | undefined,
			sorting: SortingState | undefined,
			pagination: PaginationState | false,
			quickSearch: string | undefined,
			signal?: AbortSignal
		): Promise<Properties> => {
			const params = generatePropertyQueryParams(pagination, {
				filters,
				sorting,
				quickSearch,
				accessors,
				propertyAttributes,
			});

			return request<Properties>("GET", `/api/v3/teams/${team.id}/properties`, params, { signal });
		};

		const createPropertyExport = (
			team: Team,
			accessors: Accessor[] | "*" | undefined,
			propertyAttributes: PropertyAttribute[] | "*" | undefined,
			params: { filters: Filter[] } | { ids: number[] } | {} = {},
			order: (Accessor | PropertyAttribute)[] | undefined = undefined
		) =>
			request<Export>("POST", `/api/v3/teams/${team.id}/properties/export`, {
				...params,
				accessors,
				property_attributes: propertyAttributes,
				property_page: `${window.location.origin}/epc-data`,
				...(order ? { order } : {}),
			});

		const fetchExports = (team: Team) => request<Export[]>("GET", `/api/v3/teams/${team.id}/exports`);

		const fetchExport = (team: Team, exportId: number) =>
			request<Export>("GET", `/api/v3/teams/${team.id}/exports/${exportId}`);

		const deleteExport = (team: Team, exportId: number) =>
			request<void>("DELETE", `/api/v3/teams/${team.id}/exports/${exportId}`);

		const downloadExport = (team: Team, exportId: number) =>
			self.downloadFile(
				self.get(`/api/v3/teams/${team.id}/exports/${exportId}/download`),
				"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
			);

		const getTeamAggregates = async (team: Team) => {
			return request<Aggregates>("GET", `/api/v3/teams/${team.id}/properties/aggregates?accessors=*`);
		};

		const updateTeamProperties = async (team: Team, ids: number[], data: Partial<Record<Accessor, Scalar>>) => {
			return request<{ success: boolean }>("PATCH", `/api/v3/teams/${team.id}/properties`, {
				properties: ids,
				data,
			});
		};

		const updateProperty = (
			team: Team,
			property: Property,
			data: Partial<Record<Accessor | PropertyAttribute, Scalar>>
		) => {
			return request<Property>("PATCH", `/api/v3/teams/${team.id}/properties/${property.id}`, data);
		};

		const deleteProperties = (team: Team, ids: number[]) => {
			return request<Response>("DELETE", `/api/v3/teams/${team.id}/properties`, {
				properties: ids,
			});
		};

		const getEditableFieldValues = async () => {
			return request<EditableFieldValueCollection>("GET", "/api/v3/properties/editable-field-values");
		};

		const fetchReports = (team: Team): Promise<Report[]> => {
			return request("GET", `/api/v3/teams/${team.id}/reports`);
		};
		const fetchReport = (team: Team, reportId: number): Promise<FetchReportResponse> => {
			return request("GET", `/api/v3/teams/${team.id}/reports/${reportId}`);
		};

		return {
			createPropertyQuery,
			getPropertiesViaQuery,
			getTeamAggregates,
			createPropertyExport,
			fetchExports,
			fetchExport,
			deleteExport,
			downloadExport,
			updateTeamProperties,
			getProperties,
			updateProperty,
			deleteProperties,
			getEditableFieldValues,
			fetchReports,
			fetchReport,
		};
	}

	fetchCustomImprovements(
		order?: "alphabetically",
		direction?: "asc" | "desc",
		filter?: ("include_hidden" | "parameterised" | "no_custom_equivalent")[]
	): Promise<Response> {
		const queryString = new URLSearchParams(
			Object.assign(
				{},
				order && { order },
				order && direction && { direction },
				filter && filter.reduce((acc, curr) => Object.assign(acc, { [curr]: true }), {})
			)
		);
		const hasQuery = !queryString.keys().next().done;
		return this.get("/api/custom-improvements" + (hasQuery ? `?${queryString}` : ""));
	}

	fetchCustomImprovementPresets(): Promise<Response> {
		return this.get("/api/custom-improvements/presets");
	}

	fetchEpcDataOverview(teamId: number): Promise<Response> {
		return this.get(`/api/teams/${teamId}/widgets/epc-data-overview`);
	}

	fetchGroups(teamId: number): Promise<Response> {
		return this.get(`/api/teams/${teamId}/groups`);
	}

	fetchGroup(teamId: number, groupId: number): Promise<Response> {
		return this.get(`/api/teams/${teamId}/groups/${groupId}`);
	}

	fetchNetZeroGoal(teamId: number): Promise<Response> {
		return this.get(`/api/teams/${teamId}/widgets/net-zero-goal`);
	}

	fetchNetZeroJourney(teamId: number): Promise<Response> {
		return this.get(`/api/teams/${teamId}/widgets/net-zero-journey`);
	}

	fetchSapJourney(teamId: number): Promise<Response> {
		return this.get(`/api/teams/${teamId}/widgets/sap-journey`);
	}

	fetchStats(teamId: number): Promise<Response> {
		return this.get(`/api/teams/${teamId}/widgets/stats`);
	}

	fetchLeaderboards(teamId: number): Promise<Response> {
		return this.get(`/api/teams/${teamId}/widgets/leaderboards`);
	}

	uploadImport(teamId: number, formData: FormData): Promise<Response> {
		return this.upload(`/api/teams/${teamId}/properties/import`, formData);
	}

	uploadPropertyImages(property: Property, formData: FormData): Promise<Response> {
		return this.upload(`/api/teams/${property.teamId}/properties/${property.id}/images`, formData);
	}

	updateTeamProperties(teamId: number, data: any): Promise<Response> {
		return this.put(`/api/teams/${teamId}/properties`, data);
	}

	createTeam(name: string): Promise<Response> {
		return this.post(`/api/teams`, {
			name,
		});
	}

	fetchGroupProperties(teamId: number, groupId: number): Promise<Response> {
		return this.get(`/api/teams/${teamId}/groups/${groupId}/properties`);
	}

	updatePropertySapData(teamId: number, propertyId: number, debug: boolean = false): Promise<Response> {
		const debugQuery = debug ? "?debug=true" : "";
		return this.put(`/api/teams/${teamId}/properties/${propertyId}/update_sap${debugQuery}`);
	}

	fetchJobBatches(teamId: number): Promise<Response> {
		return this.get(`/api/teams/${teamId}/batch-jobs`);
	}

	fetchJobBatchAggregate(teamId: number, batchIds: string[]): Promise<Response> {
		const queryParam = queryString.stringify({ batch_ids: batchIds }, { arrayFormat: "bracket" });
		return this.get(`/api/teams/${teamId}/batch-jobs/aggregate?${queryParam}`);
	}

	fetchJobBatch(teamId: number, batchUuid: string): Promise<Response> {
		return this.get(`/api/teams/${teamId}/batch-jobs/${batchUuid}`);
	}

	deleteJobBatch(teamId: number, batchUuid: string): Promise<Response> {
		return this.destroy(`/api/teams/${teamId}/batch-jobs/${batchUuid}`);
	}

	deleteCustomImprovementSet(teamId: number, propertyId: number, setId: number): Promise<Response> {
		return this.destroy(`/api/teams/${teamId}/properties/${propertyId}/custom-improvement-sets/${setId}`);
	}

	updateProperty(teamId: number, propertyId: number, data: any): Promise<Response> {
		return this.put(`/api/teams/${teamId}/properties/${propertyId}`, data);
	}

	updateUser(userId: number, data: any): Promise<Response> {
		return this.put(`/api/users/${userId}`, data);
	}

	deleteTeamProperties(teamId: number, propertyIds: number[]): Promise<Response> {
		return this.destroy(`/api/teams/${teamId}/properties`, propertyIds);
	}

	deleteGroup(teamId: number, groupId: number): Promise<Response> {
		return this.destroy(`/api/teams/${teamId}/groups/${groupId}`);
	}

	updateGroup(teamId: number, groupId: number, data: any): Promise<Response> {
		return this.put(`/api/teams/${teamId}/groups/${groupId}`, data);
	}

	fetchTeams(): Promise<Response> {
		return this.get("/api/teams");
	}

	fetchTeam(id: number): Promise<Response> {
		return this.get(`/api/teams/${id}`);
	}

	updateTeamUsers(teamId: number, data: any): Promise<Response> {
		return this.post(`/api/teams/${teamId}/users`, data);
	}

	deleteTeamUser(teamId: number, userId: number): Promise<Response> {
		return this.destroy(`/api/teams/${teamId}/users/${userId}`);
	}

	updateTeamUser(teamId: number, userId: number, data: any): Promise<Response> {
		return this.put(`/api/teams/${teamId}/users/${userId}`, data);
	}

	fetchEditableFieldValues(): Promise<Response> {
		return this.get("/api/properties/editable-field-values");
	}

	fetchTeamPropertyAggregates(teamId: number): Promise<Response> {
		return this.get(`/api/teams/${teamId}/properties/aggregates`);
	}

	createGroupFromUprnSpreadsheetUpload(teamId: number, formData: FormData) {
		return this.upload(`/api/teams/${teamId}/groups`, formData);
	}

	fetchTeamGroups(teamId: number): Promise<Response> {
		return this.get(`/api/teams/${teamId}/groups`);
	}

	fetchImport(id: number): Promise<Response> {
		return this.get(`/api/imports/${id}`);
	}

	deleteProperty(teamId: number, propertyId: number): Promise<Response> {
		return this.destroy(`/api/teams/${teamId}/properties/${propertyId}`);
	}

	fetchProperty(teamId: number, propertyId: number): Promise<Response> {
		return this.get(`/api/teams/${teamId}/properties/${propertyId}`);
	}

	searchEpcCertificates(teamId: number, propertyId: number, signal?: AbortSignal): Promise<Response> {
		return this.get(`/api/teams/${teamId}/properties/${propertyId}/update-epc/search`, { signal });
	}

	createGroup(teamId: number, data: any): Promise<Response> {
		return this.post(`/api/teams/${teamId}/groups`, data);
	}

	updateGroupProperties(teamId: number, groupId: number, data: any): Promise<Response> {
		return this.put(`/api/teams/${teamId}/groups/${groupId}/properties`, data);
	}

	deleteGroupProperties(teamId: number, groupId: number, data: any): Promise<Response> {
		return this.destroy(`/api/teams/${teamId}/groups/${groupId}/properties`, data);
	}

	fetchMe(): Promise<Response> {
		return this.get("/api/users/me");
	}

	createPersonalServiceAccountToken(): Promise<Response> {
		return this.post("/api/service-accounts/personal-token", {});
	}

	fetchSuggestedCustomImprovements(teamId: number, propertyId: number): Promise<Response> {
		return this.get(`/api/teams/${teamId}/properties/${propertyId}/custom-improvements/suggested`);
	}

	createPropertyCustomImprovements(property: Property, data: any): Promise<Response> {
		return this.post(`/api/teams/${property.teamId}/properties/${property.id}/custom-improvement-sets`, data);
	}

	createMultiPropertyCustomImprovements(teamId: number, data: any): Promise<Response> {
		return this.post(`/api/teams/${teamId}/properties/custom-improvement-sets`, data);
	}

	fetchAllPropertySmartTargets(
		teamId: number,
		propertyId: number,
		include?: AllSmartTargetParameters[]
	): Promise<Response> {
		const params = new URLSearchParams();
		if (include?.length) {
			include.forEach((param) => params.set(param, ""));
		}
		return this.get(`/api/teams/${teamId}/properties/${propertyId}/smart-targets?${params.toString()}`);
	}

	fetchPropertySmartTargets(teamId: number, propertyId: number, smartTargetId: number): Promise<Response> {
		return this.get(`/api/teams/${teamId}/properties/${propertyId}/smart-targets/${smartTargetId}`);
	}

	createSmartTargets(teamId: number, property: Property, data: any): Promise<Response> {
		return this.post(`/api/teams/${teamId}/properties/${property.id}/smart-targets`, data);
	}

	createMultiPropertySmartTargets(teamId: number, data: any): Promise<Response> {
		return this.post(`/api/teams/${teamId}/properties/smart-target-batch`, data);
	}

	deleteSmartTargets(teamId: number, property: Property, smartTarget: SmartTarget): Promise<Response> {
		return this.destroy(`/api/teams/${teamId}/properties/${property.id}/smart-targets/${smartTarget.id}`);
	}

	cancelPendingSmartTargets(teamId: number, property: Property, smartTarget: SmartTarget): Promise<Response> {
		return this.destroy(`/api/teams/${teamId}/properties/${property.id}/smart-targets/${smartTarget.id}/cancel`);
	}

	deletePropertyImages(property: Property): Promise<Response> {
		return this.destroy(`/api/teams/${property.teamId}/properties/${property.id}/images`);
	}

	deletePropertyImage(property: Property, image: PropertyImage): Promise<Response> {
		return this.destroy(`/api/teams/${property.teamId}/properties/${property.id}/images/${image.id}`);
	}

	downloadTeamProperties(teamId: number, json?: IPropertyDownloadQuery): Promise<void> {
		return this.downloadPost(`/api/teams/${teamId}/properties/export?`, "text/csv", json);
	}

	downloadGroupProperties(teamId: number, groupId: number, query?: IPropertyDownloadQuery): Promise<void> {
		return this.download(
			`/api/teams/${teamId}/groups/${groupId}/properties/export?` + queryString.stringify(query ?? {}),
			"text/csv"
		);
	}

	fetchPropertyColumns(teamId: number): Promise<Response> {
		return this.get(`/api/v3/teams/${teamId}/properties/columns`);
	}
}
